import { CheckCircleTwoTone, DollarTwoTone, QuestionCircleTwoTone } from '@ant-design/icons'
import { Card, Checkbox, Collapse, Layout, List, Progress, Space, Tag, Typography } from 'antd'
import { Header, Content } from 'antd/es/layout/layout'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { HeaderMenu } from '../../components/header-menu'
import { financialLiteracyChecklistItems as checklistItems } from './adulting-101-checklists'

const { Title, Paragraph } = Typography

const faqItems = [
  {
    question: 'Why should I bother tracking every dollar I spend?',
    answer: (
      <>
        You can't manage money you don't understand. Tracking spending reveals patterns — like how much you spend on food or apps — that
        you'd otherwise never notice. It's the same awareness that helps with{' '}
        <Link to="/adulting-101/time-management">Time Management</Link>, where tracking where your hours go reveals where they're being
        wasted.
      </>
    ),
  },
  {
    question: 'How do I create a budget?',
    answer: (
      <>
        Write down how much money comes in, then list everything you spend money on. If spending is more than income, you need to cut back.
        Building this habit is a form of <Link to="/adulting-101/responsibility">Responsibility</Link> — taking ownership of your own money
        instead of hoping it works out.
      </>
    ),
  },
  {
    question: 'How much should I be saving?',
    answer: (
      <>
        A common rule is to save 20% of what you earn, spend 50% on needs, and 20% on wants. Even saving 10% is better than nothing.
        Building this habit early is like keeping up with <Link to="/adulting-101/household-care">Household Care</Link> — a little
        consistent effort now prevents a much bigger problem later.
      </>
    ),
  },
  {
    question: 'How do I tell the difference between a need and a want?',
    answer: (
      <>
        A need is something you can't function without (food, shelter, basic clothing). A want makes life more enjoyable but isn't
        essential. Pausing to ask this question before buying something is the same kind of self-control covered in{' '}
        <Link to="/adulting-101/emotional-intelligence">Emotional Intelligence</Link>.
      </>
    ),
  },
  {
    question: 'Is debt always bad?',
    answer: (
      <>
        Some debt is necessary (student loans, a mortgage), but high-interest debt like credit cards or payday loans can trap you. Avoiding
        debt for things that don't improve your future takes the same discipline described in{' '}
        <Link to="/adulting-101/problem-solving">Problem Solving</Link> — thinking through consequences before acting.
      </>
    ),
  },
  {
    question: 'Should I always research before making a big purchase?',
    answer: (
      <>
        Yes. Comparing prices, reading reviews, and waiting a day or two before a big purchase prevents impulse buys you'll regret. This
        patience mirrors the kind of thoughtful decision-making covered in <Link to="/adulting-101/like-love-lust">Like, Love, Lust</Link> —
        not rushing into big decisions based on emotion alone.
      </>
    ),
  },
  {
    question: "What's the difference between credit cards and debit cards, and why do the extra costs matter?",
    answer: (
      <>
        A debit card only lets you spend what you have. A credit card lets you borrow, but you pay interest and fees if you don't pay it
        back quickly. Understanding the full cost before committing to anything is a skill that also matters in{' '}
        <Link to="/adulting-101/friends-and-family">Friends and Family</Link> when you consider what a favor or commitment really costs you.
      </>
    ),
  },
  {
    question: 'Why do I need an emergency fund if I already have savings goals?',
    answer: (
      <>
        An emergency fund is separate from regular savings — it's there so a surprise expense (a car repair, a medical bill) doesn't force
        you into debt. Preparing for the unexpected is part of good <Link to="/adulting-101/spiritual-awareness">Spiritual Awareness</Link>{' '}
        too — having peace of mind because you're prepared.
      </>
    ),
  },
]

export function Adulting101FinancialLiteracyPage() {
  const [completedIds, setCompletedIds] = useState<Set<string>>(() => new Set())
  const completedCount = completedIds.size
  const completionPercent = Math.round((completedCount / checklistItems.length) * 100)

  return (
    <Layout>
      <Header>
        <h1>Financial Literacy</h1>
      </Header>
      <HeaderMenu selected="adulting-101" />
      <Content style={{ padding: '20px', textAlign: 'left', background: 'linear-gradient(180deg, #f6ffed 0%, #ffffff 220px)' }}>
        <Title level={2}>Financial literacy is understanding how money works and making smart decisions about it.</Title>
        <Paragraph type="secondary">
          People who manage money wisely have less stress, more independence, and more choices in life. Whether you earn a little or a lot,
          learning to budget, save, and avoid debt will set you up for success.
        </Paragraph>

        <Card
          title={
            <Space>
              <DollarTwoTone twoToneColor="#52c41a" />
              Financial Literacy Checklist
            </Space>
          }
          style={{ marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
          extra={<Progress type="circle" percent={completionPercent} size={56} />}
        >
          <List
            dataSource={checklistItems}
            renderItem={(item) => (
              <List.Item>
                <Checkbox
                  checked={completedIds.has(item.id)}
                  onChange={(event) => {
                    setCompletedIds((currentIds) => {
                      const nextIds = new Set(currentIds)
                      if (event.target.checked) {
                        nextIds.add(item.id)
                      } else {
                        nextIds.delete(item.id)
                      }
                      return nextIds
                    })
                  }}
                >
                  {item.text}
                </Checkbox>
              </List.Item>
            )}
          />
          {completionPercent === 100 ? (
            <Tag color="success" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} style={{ marginTop: '12px' }}>
              Congratulations, you're thinking financially smart!
            </Tag>
          ) : (
            <Paragraph style={{ marginTop: '12px' }}>
              Financial skills take time to develop, but every small decision about money shapes your future. Start where you are and keep
              learning.
            </Paragraph>
          )}
        </Card>

        <Card
          title={
            <Space>
              <QuestionCircleTwoTone />
              FAQ: Financial Literacy
            </Space>
          }
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        >
          <Collapse
            items={faqItems.map((faq, index) => ({
              key: index,
              label: faq.question,
              children: <Paragraph>{faq.answer}</Paragraph>,
            }))}
          />
        </Card>
      </Content>
    </Layout>
  )
}
